import URLSearchParams from '@ungap/url-search-params';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactPaginate from 'react-paginate';
import { connect } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { RootState } from '../../../store';
import { IOrderDetails } from '../../../models/product-detail';
import { addToCart } from '../../../store/product/product-detail-actions';
import { loadProducts } from '../../../store/product/product-list-actions';
import CheckboxList from '../../inputs/checkbox-list';
import { IHash } from './product-list-types';
import './product-list.css';
import SelectInputSimple from '../../inputs/select-input-simple';
import TransferPatientAlert from '../../common/transfer-patient-alert';
import ProductItem from './product-item';
import ImportantMessage from '../../inputs/message-important';
import { agreementDisplayCheck } from '../../../hooks/agreement-hook';
import { isMiracleEar } from '../../../hooks/miracle-ear-hook';
import * as CONSTANTS from '../../common/constants';
import { isStarkey } from '../../../hooks/starkey-hook';
import { isAudibel } from '../../../hooks/audibel-hook';
import { isNuEar } from '../../../hooks/nuear-hook';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux-hooks';
import { usePiTracker } from '../../../hooks/pi-tracker';
import { useLoadFavorites } from '../../../hooks/use-is-favorite';
import ProductGuidePDF from './ProductGuidePDF';
import { useLocation } from 'react-router-dom';
// TODO:
//   1) Add translations to all files under parent folder
//   2) Do validation for Program Information correctly (currently it's a bit of a hack)
//

interface IProductListProps {
    attributeDefinitions: string;
    categoryId: string;
    categoryName: string;
    query: string;
    menuType: string;
    isCustom: boolean;
    addToCart: (order: IOrderDetails) => Promise<boolean>;
    setIsBusy: (isBusy: boolean) => Promise<void>;
}

export const ProductList: React.FC<IProductListProps> = ({
    attributeDefinitions,
    categoryId,
    categoryName,
    query,
    menuType,
    isCustom,
    addToCart,
}) => {
    const intl = useIntl();
    const [pageTitle, setPageTitle] = useState<string>(menuType);
    const [visibleFilters, setVisibleFilters] = useState<boolean>(false);
    const [resetDisabled, setResetDisabled] = useState<boolean>(true);
    const [selectedAttrValIds, setSelectedAttrValIds] = useState<IHash>({});
    const [searchString, setSearchString] = useState<string>(query);
    const [searchStringLastSearched, setSearchStringLastSearched] =
        useState<string>(query);
    const [selectedAgreementName, setSelectedAgreementName] =
        useState<string>('');
    const [otherAgreementNameSelected, setOtherAgreementNameSelected] =
        useState<boolean>(false);

    const [programInformation, setProgramInformation] = useState<string>('');
    const [programInformationError, setProgramInformationError] =
        useState<boolean>(false);

    const products = useAppSelector((state: RootState) =>
        state.products ? state.products.data! : null
    );
    const selectedBrand = useAppSelector(
        (state: RootState) => state.session!.data!.selectedBrand
    );
    const maxQuantity = useAppSelector(
        (state: RootState) => state.session!.data!.settings!.MaxQuantity
    );
    const session = useAppSelector((state: RootState) =>
        state.session ? state.session.data! : null
    );

    useLoadFavorites();
    const location = useLocation();

    const dispatch = useAppDispatch();

    const loadInitialProducts = () => {
        dispatch(
            loadProducts(
                categoryId,
                categoryName,
                query,
                '',
                attributeDefinitions,
                isCustom,
                1
            )
        );
        if (
            !attributeDefinitions &&
            !categoryId &&
            !categoryName &&
            query &&
            !menuType
        ) {
            setPageTitle('Searching for: ' + query);
        } else if (!query) {
            setSearchString('');
            setSearchStringLastSearched('');
        }
        setSelectedAgreementName('');
    };

    const handleGoToProductDetail = (e: any) => {
        if (session!.country === CONSTANTS.CA) {
            if (selectedAgreementName === '') {
                setProgramInformationError(true);
                e.preventDefault();
            } else {
                setProgramInformationError(false);
            }
        } else {
            if (otherAgreementNameSelected && programInformation === '') {
                setProgramInformationError(true);
                e.preventDefault();
            }
        }
    };

    // #region useEffects
    usePiTracker();

    useEffect(() => {
        loadInitialProducts();
        if (menuType) {
            setPageTitle(
                intl.formatMessage({
                    id: menuType.split(' ').join('_'),
                    defaultMessage: menuType,
                })
            );
        }
    }, [attributeDefinitions, categoryId, categoryName, query, selectedBrand]);

    useEffect(() => {
        if (products && products.attributeTypes) {
            const hash: IHash = {};
            products.attributeTypes.map(
                (type) =>
                    (hash[type.english] = type.values
                        .filter((value) => value.selected === true)
                        .map((value) => value.name))
            );
            setSelectedAttrValIds(hash);
        }
    }, [products]);
    // #endregion useEffects

    // #region Agreements
    const getAttributeDefinitions = (agreementNameSelected: string) => {
        const agreementNameAttrDefn =
            agreementNameSelected.length > 0
                ? 'Agreement:' +
                  products!.agreements.find(
                      (x) => x.agreementName === agreementNameSelected
                  )!.agreementId
                : '';

        if (attributeDefinitions.length === 0) {
            return agreementNameAttrDefn;
        }

        return agreementNameAttrDefn.length === 0
            ? attributeDefinitions
            : attributeDefinitions + ',' + agreementNameAttrDefn;
    };
    const caAgreementCheck = (name: string) => {
        if (session!.country === CONSTANTS.CA) {
            return '';
        }
        return name;
    };
    const handleAgreementNameChange = (e: any) => {
        const agreementNameSelected = e.target.value as string;
        setSelectedAgreementName(agreementNameSelected);
        const otherSelected = agreementNameSelected.toUpperCase() === 'OTHER';
        setOtherAgreementNameSelected(otherSelected);
        setProgramInformation('');
        setProgramInformationError(false);
        if (session!.country !== CONSTANTS.CA) {
            if (
                session!.selectedBrand === CONSTANTS.AUDIBEL &&
                attributeDefinitions.includes(CONSTANTS.FAMILY)
            ) {
                const attributesArr = attributeDefinitions
                    .split(',')
                    .filter(
                        (attribute) => !attribute.includes(CONSTANTS.FAMILY)
                    );
                attributeDefinitions = attributesArr.toString();
            }
            if (menuType === 'Hearing Aids' || query) {
                dispatch(
                    loadProducts(
                        categoryId,
                        categoryName,
                        searchStringLastSearched,
                        getSelectedAttributeValueIdsString(),
                        getAttributeDefinitions(
                            caAgreementCheck(agreementNameSelected)
                        ),
                        isCustom,
                        1
                    )
                );
            }
        }
    };

    const handleProgramInformationChange = (e: any) => {
        const programInfo = e.target.value as string;
        setProgramInformation(programInfo);
        setProgramInformationError(programInfo.length === 0);
    };

    const getProductDetailUrl = (productId: string) => {
        return (
            '/estore/product-detail/' +
            productId +
            '?agreementName=' +
            encodeURIComponent(selectedAgreementName) +
            '&agreementInfo=' +
            encodeURIComponent(programInformation) +
            '&patientNumberRequired=' +
            isPatientNumberRequired()
        );
    };

    const isPatientNumberRequired = () => {
        const agreement = products!.agreements.find(
            (x) => x.agreementName === selectedAgreementName
        );
        return agreement ? agreement.patientNumberRequired : false;
    };

    // #endregion Agreements

    // #region Filters
    const getFiltersText = () => {
        return visibleFilters
            ? intl.formatMessage({ id: 'Hide_filters' })
            : intl.formatMessage({ id: 'Show_filters' });
    };

    const toggleFilter = () => {
        setVisibleFilters(!visibleFilters);
    };

    const getSelectedAttributeValueIdsString = () => {
        return JSON.stringify(selectedAttrValIds);
    };

    const handleAttributeValuesChanged = (e: any) => {
        const selectedIds = Object.assign({}, selectedAttrValIds);
        selectedIds[e.target.english] = e.target.value;
        setSelectedAttrValIds(selectedIds);
        const selectedIdsString = JSON.stringify(selectedIds);

        let disable = resetDisabled;
        for (const key in selectedIds) {
            if (selectedIds[key].length !== 0) {
                disable = false;
                break;
            } else {
                disable = true;
            }
        }
        setResetDisabled(disable);

        dispatch(
            loadProducts(
                categoryId,
                categoryName,
                searchStringLastSearched,
                selectedIdsString,
                getAttributeDefinitions(
                    caAgreementCheck(selectedAgreementName)
                ),
                isCustom,
                1
            )
        );
    };

    const reset = () => {
        const ids = Object.assign({}, selectedAttrValIds);
        for (const key in ids) {
            ids[key] = [];
        }
        setSelectedAttrValIds(ids);
        setResetDisabled(true);

        dispatch(
            loadProducts(
                categoryId,
                categoryName,
                searchStringLastSearched,
                '',
                getAttributeDefinitions(
                    caAgreementCheck(selectedAgreementName)
                ),
                isCustom,
                1
            )
        );
    };
    // #endregion Filters

    // #region Search
    const search = () => {
        dispatch(
            loadProducts(
                categoryId,
                categoryName,
                searchString,
                getSelectedAttributeValueIdsString(),
                getAttributeDefinitions(
                    caAgreementCheck(selectedAgreementName)
                ),
                isCustom,
                1
            )
        );
        setSearchStringLastSearched(searchString);
    };

    const handleSearchChange = (e: any) => {
        setSearchString(e.target.value);
    };

    const handleSearchKeyPress = (e: any) => {
        if (e.key === 'Enter') {
            search();
        }
    };

    const handleSearch = () => {
        search();
    };
    // #endregion Search

    // #region Pagination
    const handlePageChange = (selectedItem: { selected: number }) => {
        const pageNumber = selectedItem.selected + 1;
        dispatch(
            loadProducts(
                categoryId,
                categoryName,
                searchStringLastSearched,
                getSelectedAttributeValueIdsString(),
                getAttributeDefinitions(
                    caAgreementCheck(selectedAgreementName)
                ),
                isCustom,
                pageNumber
            )
        );
    };
    // #endregion Pagination
    const checkOTCMessage = () => {
        if (
            products &&
            products.results &&
            products.results.length > 0 &&
            products.results[0].url.toLowerCase().indexOf('otc') > -1
        ) {
            if (
                isStarkey(session!.siteNumber) ||
                isAudibel(session!.siteNumber) ||
                isNuEar(session!.siteNumber)
            ) {
                return true;
            }
            return false;
        }
        return false;
    };

    return (
        <>
            <TransferPatientAlert />
            {products && (
                <div id="wrapper" style={{ border: '0px solid black' }}>
                    {pageTitle && (
                        <div style={{ textAlign: 'center' }}>
                            <h1>
                                {products &&
                                products.results &&
                                products.results.length > 0
                                    ? pageTitle
                                    : intl.formatMessage({
                                          id: 'No_results_found',
                                          defaultMessage: 'No results found',
                                      })}
                            </h1>
                            {!CONSTANTS.NO_BATTERY_MESSAGE_COUNTRIES.includes(
                                session!.country
                            ) &&
                                products &&
                                products.results &&
                                products.results.length > 0 &&
                                products.results[0].url
                                    .toLowerCase()
                                    .indexOf('batteries') > -1 && (
                                    <span
                                        style={{
                                            textAlign: 'center',
                                            fontSize: '20px',
                                        }}
                                    >
                                        <FormattedMessage
                                            id="Contact_battery_rep_for_custom_labels"
                                            defaultMessage="Batteries will begin transitioning to federally required Child Resistant Packaging (CRP) beginning March 8, 2024. You may continue to receive non-CRP batteries as this transition begins. Please contact your battery representative with any questions."
                                        />
                                    </span>
                                )}
                            {checkOTCMessage() && (
                                <span
                                    style={{
                                        textAlign: 'center',
                                        fontSize: '20px',
                                    }}
                                >
                                    <FormattedMessage
                                        id="Please_contact_your_sales_OTC"
                                        defaultMessage="Please contact your sales representative to ask about the OTC Starter Kit to get your office OTC-ready."
                                    />
                                </span>
                            )}
                        </div>
                    )}
                    <div
                        className="row"
                        style={{
                            marginTop: '40px',
                            marginBottom: '20px',
                        }}
                    >
                        <div className="col-md-3">
                            {products &&
                                products.attributeTypes &&
                                products.attributeTypes.length > 0 && (
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={toggleFilter}
                                    >
                                        <i className="fa fa-filter" />{' '}
                                        {getFiltersText()}
                                    </button>
                                )}
                        </div>
                        <div
                            className="col-md-4"
                            style={{ paddingRight: '15px' }}
                        >
                            <div className="input-group">
                                <input
                                    type="text"
                                    name="searchString"
                                    className="form-control"
                                    placeholder={intl.formatMessage({
                                        id: 'Enter_keyword',
                                        defaultMessage: 'Enter keyword',
                                    })}
                                    value={searchString}
                                    onChange={handleSearchChange}
                                    onKeyPress={handleSearchKeyPress}
                                />
                                <span className="input-group-addon">
                                    <button
                                        className="button-label"
                                        type="button"
                                        style={{
                                            borderStyle: 'solid',
                                            borderColor: 'transparent',
                                            backgroundColor: 'transparent',
                                        }}
                                        title="Search"
                                        onClick={handleSearch}
                                    >
                                        <i className="fa fa-search" />
                                    </button>
                                </span>
                            </div>
                        </div>
                        {agreementDisplayCheck(session!) && (
                            <div className="col-md-5">
                                {products &&
                                    products.agreements &&
                                    products.agreements.length > 0 && (
                                        <>
                                            <div className="row">
                                                <div
                                                    className="form-group col-md-4"
                                                    style={{
                                                        textAlign: 'right',
                                                        paddingRight: '0px',
                                                    }}
                                                >
                                                    <label
                                                        className="control-label"
                                                        style={{
                                                            paddingTop: '8px',
                                                            textTransform:
                                                                'none',
                                                            cursor: 'default',
                                                        }}
                                                        htmlFor="agreements"
                                                    >
                                                        <FormattedMessage id="Benefit_Name" />
                                                        :
                                                    </label>
                                                </div>

                                                <div className="col-md-8">
                                                    <SelectInputSimple
                                                        name="agreements"
                                                        options={products.agreements.map(
                                                            (agreement) => ({
                                                                value: agreement.agreementName,
                                                                text: agreement.agreementName,
                                                            })
                                                        )}
                                                        hasDefaultOption={true}
                                                        defaultOption={'Select'}
                                                        value={
                                                            selectedAgreementName
                                                        }
                                                        onChange={
                                                            handleAgreementNameChange
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            {otherAgreementNameSelected &&
                                                session!.country !==
                                                    CONSTANTS.CA && (
                                                    <div className="row">
                                                        <div
                                                            className="form-group col-md-4"
                                                            style={{
                                                                textAlign:
                                                                    'right',
                                                                paddingRight:
                                                                    '0px',
                                                            }}
                                                        >
                                                            <label
                                                                className="control-label"
                                                                style={{
                                                                    paddingTop:
                                                                        '8px',
                                                                    textTransform:
                                                                        'none',
                                                                    cursor: 'default',
                                                                }}
                                                                htmlFor="programInformation"
                                                            >
                                                                <FormattedMessage id="Program_Information" />
                                                                :
                                                            </label>
                                                        </div>
                                                        <div className="col-md-8">
                                                            <div className="input-group">
                                                                <input
                                                                    type="text"
                                                                    name="programInformation"
                                                                    className="form-control"
                                                                    value={
                                                                        programInformation
                                                                    }
                                                                    onChange={
                                                                        handleProgramInformationChange
                                                                    }
                                                                    required
                                                                />
                                                                <span className="input-group-addon">
                                                                    <span
                                                                        className="fas fa-asterisk required-red"
                                                                        style={{
                                                                            fontSize:
                                                                                '8px',
                                                                        }}
                                                                    />
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                        </>
                                    )}
                            </div>
                        )}
                    </div>
                    {programInformationError && (
                        <div className="row">
                            <div
                                className="col-md-12 alert alert-danger"
                                style={{
                                    margin: '0px 0px 20px 0px',
                                }}
                            >
                                {session!.country === CONSTANTS.CA &&
                                selectedAgreementName === '' ? (
                                    <FormattedMessage
                                        id="Benefit_Name_is_required"
                                        defaultMessage="Benefit Name is required"
                                    />
                                ) : (
                                    <FormattedMessage id="Program_Information_is_required" />
                                )}
                            </div>
                        </div>
                    )}
                    {menuType === 'Packaging Supplies' &&
                        session!.country === CONSTANTS.US && (
                            <ImportantMessage
                                message={intl.formatMessage({
                                    id: 'Instructions_Shipping_Rechargeable_Products',
                                    defaultMessage:
                                        'For instructions on shipping rechargeable products, view our ',
                                })}
                                boxWidth={650}
                                url="/pdfs/Rechargeable_Shipping_Guide.pdf"
                                urlText={intl.formatMessage({
                                    id: 'Rechargeable_Shipping_Guide',
                                    defaultMessage:
                                        'Rechargeable Shipping Guide',
                                })}
                            ></ImportantMessage>
                        )}
                    {products &&
                        products.attributeTypes &&
                        products.attributeTypes.length > 0 && (
                            <CSSTransition
                                in={visibleFilters}
                                timeout={500}
                                classNames="options-panel"
                                unmountOnExit={true}
                            >
                                <div
                                    className="panel panel-default"
                                    style={{
                                        padding: '20px',
                                        backgroundColor: '#F5F5F5',
                                    }}
                                >
                                    <div className="row">
                                        {products &&
                                            products.attributeTypes &&
                                            products.attributeTypes.map(
                                                (type, idx) => {
                                                    return (
                                                        <div
                                                            className="col-md-2"
                                                            key={idx}
                                                        >
                                                            <span
                                                                style={{
                                                                    fontWeight:
                                                                        'bold',
                                                                }}
                                                            >
                                                                {type.name}
                                                            </span>
                                                            <CheckboxList
                                                                name={type.name}
                                                                english={
                                                                    type.english
                                                                }
                                                                checkedItems={
                                                                    selectedAttrValIds[
                                                                        type
                                                                            .english
                                                                    ]
                                                                }
                                                                items={type.values.map(
                                                                    (
                                                                        value
                                                                    ) => ({
                                                                        label: value.name,
                                                                        name: value.name,
                                                                        value: value.name,
                                                                    })
                                                                )}
                                                                onChange={
                                                                    handleAttributeValuesChanged
                                                                }
                                                            />
                                                        </div>
                                                    );
                                                }
                                            )}
                                    </div>
                                    <div className="row">
                                        <div className="col-md-10" />
                                        <div className="col-md-2">
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={() => {
                                                    reset();
                                                }}
                                                disabled={resetDisabled}
                                            >
                                                <FormattedMessage id="Reset" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </CSSTransition>
                        )}
                    {products &&
                        products.flexContent &&
                        !isMiracleEar(session!.siteNumber) && (
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: products.flexContent,
                                }}
                            />
                        )}
                    {products &&
                        CONSTANTS.ACCESSORY_GUIDE_COUNTRIES.includes(
                            session!.country
                        ) &&
                        CONSTANTS.ACCESSORY_GUIDE_BRANDS.includes(
                            session!.selectedBrand
                        ) &&
                        !isMiracleEar(session!.siteNumber) &&
                        location.search.includes('Rechargeable') && (
                            <ProductGuidePDF
                                linkKey={'rechargeable_accessory_guide'}
                                titleDefault={'{0} Accessory Guide'}
                                descriptionDefault={`Use this guide to help determine which {0} hearing aids are compatible with certain chargers, accessories and apps.`}
                                linkDefault={
                                    'https://cdn.mediavalet.com/usil/starkeyhearingtech/GbCUfFQGUkmmW-LVUh-_rg/2WU_FUo4qUG81UhxCeeCzg/Original/2023%20Starkey%20Accessory%20Guide.pdf'
                                }
                                downloadTextDefault={
                                    'Download the {0} Accessory Guide (PDF)'
                                }
                            />
                        )}
                    {products &&
                        products.results &&
                        products.results.length > 0 && (
                            <div style={{ textAlign: 'center' }}>
                                <div
                                    style={{
                                        textAlign: 'center',
                                        justifyContent: 'left',
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        border: '0px solid black',
                                    }}
                                >
                                    {products.results.map((product, index) => {
                                        return (
                                            <ProductItem
                                                key={product.partNumber.replace(
                                                    /\W/g,
                                                    ''
                                                )}
                                                product={product}
                                                productDetailUrl={getProductDetailUrl(
                                                    product.id
                                                )}
                                                maxQuantity={
                                                    session!.isSiteUpgraded &&
                                                    menuType.toLowerCase() ===
                                                        'batteries'
                                                        ? 1000000
                                                        : maxQuantity
                                                }
                                                onGoToProductDetail={
                                                    handleGoToProductDetail
                                                }
                                                addToCart={addToCart}
                                                selectedAgreement={
                                                    selectedAgreementName
                                                }
                                                country={session!.country}
                                            />
                                        );
                                    })}
                                </div>
                                {products &&
                                    products.pagination &&
                                    products.pagination.numberOfPages > 1 && (
                                        <ReactPaginate
                                            previousLabel={intl.formatMessage({
                                                id: 'Previous',
                                                defaultMessage: 'Previous',
                                            })}
                                            nextLabel={intl.formatMessage({
                                                id: 'Next',
                                                defaultMessage: 'Next',
                                            })}
                                            breakLabel={'...'}
                                            breakClassName={'break-me'}
                                            pageCount={
                                                products.pagination
                                                    .numberOfPages
                                            }
                                            marginPagesDisplayed={1}
                                            pageRangeDisplayed={3}
                                            onPageChange={handlePageChange}
                                            containerClassName={'pagination'}
                                            activeClassName={'active'}
                                            forcePage={
                                                products.pagination
                                                    .currentPage - 1
                                            }
                                        />
                                    )}
                            </div>
                        )}
                </div>
            )}
        </>
    );
};

const mapStateToProps = (state: RootState, ownProps: any) => {
    const params = new URLSearchParams(ownProps.location.search);
    return {
        attributeDefinitions: params.get('attributeDefinitions') || '',
        categoryId: params.get('categoryId') || '',
        categoryName: params.get('categoryName') || '',
        query: params.get('query') || '',
        menuType: params.get('menuType') || '',
        isCustom:
            params.get('isCustom') &&
            params.get('isCustom')!.toLowerCase() === 'true'
                ? true
                : false,
    };
};

const mapDispatchToProps = {
    addToCart,
};

const productListContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(ProductList);

export default productListContainer;
